import axios from 'axios';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';
const DEFAULT_TENANT_ID = import.meta.env.VITE_TENANT_ID || 'default';

// Create an axios instance with default config
const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and tenant header
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Get user role from localStorage to check if tenant switching is allowed
  const userString = localStorage.getItem('user');
  const userRole = userString ? JSON.parse(userString).role : null;

  // Only super_admin users can switch tenants, otherwise use their assigned tenant or the default
  const tenantId = userRole === 'super_admin'
    ? (localStorage.getItem('tenantId') || DEFAULT_TENANT_ID)
    : (userString ? JSON.parse(userString).tenantId : DEFAULT_TENANT_ID);

  if (tenantId) {
    config.headers['X-Tenant-ID'] = tenantId;
  }

  // Add selected member profile ID for member users
  const selectedMemberProfileString = localStorage.getItem('selectedMemberProfile');
  if (selectedMemberProfileString) {
    try {
      const selectedMemberProfile = JSON.parse(selectedMemberProfileString);
      if (selectedMemberProfile?.id) {
        config.headers['X-Member-Profile-ID'] = selectedMemberProfile.id;
      }
    } catch (error) {
      console.error('Error parsing selected member profile:', error);
    }
  }

  return config;
});

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 Unauthorized errors by redirecting to login
    // Skip the auto-redirect for specific endpoints that don't require authentication
    const isAuthMeEndpoint = error.config?.url === '/auth/me';
    const isLoginEndpoint = error.config?.url === '/auth/login';
    const isPublicTenantSettingsEndpoint = error.config?.url === '/tenant-settings/public';
    
    if (error.response && error.response.status === 401 && 
        !isAuthMeEndpoint && !isLoginEndpoint && !isPublicTenantSettingsEndpoint) {
      // Only redirect if not on excluded endpoints
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Also forget the active member profile: the next person to log in on this browser must not inherit it
      localStorage.removeItem('selectedMemberProfile');
      window.location.href = '/login';
    }

    // The server refused the active member profile because it is not the logged-in user's
    // (stale selection, e.g. left behind by a previous login). Drop it and let the user pick again.
    if (error.response?.status === 403 && error.response?.data?.code === 'PROFILE_NOT_OWNED'
        && localStorage.getItem('selectedMemberProfile')) {
      localStorage.removeItem('selectedMemberProfile');
      if (!window.location.pathname.startsWith('/select-member-profile')) {
        window.location.href = '/select-member-profile';
      }
    }
    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  login: (email: string, password: string, rememberMe: boolean = false) =>
    api.post('/auth/login', { email, password, rememberMe }),

  register: (userData: any) =>
    api.post('/auth/register', userData),

  getCurrentUser: () =>
    api.get('/auth/me'),

  logout: () =>
    api.post('/auth/logout'),

  forgotPassword: (email: string, tenantName?: string) =>
    api.post('/auth/forgot-password', { email, tenantName }),

  resetPassword: (token: string, password: string, confirmPassword: string) =>
    api.post('/auth/reset-password', { token, password, confirmPassword }),

  verifyResetToken: (token: string) =>
    api.get(`/auth/verify-token/${token}`),
};

// User API
export const userAPI = {
  getUsers: (filters?: any) =>
    api.get('/users', { params: filters }),

  getCoaches: () =>
    api.get('/users/coaches'),

  getUserById: (id: string) =>
    api.get(`/users/${id}`),

  createUser: (userData: any) =>
    api.post('/users', userData),

  updateUser: (id: string, userData: any) =>
    api.put(`/users/${id}`, userData),

  resetPassword: (id: string, passwordData: { currentPassword: string; newPassword: string }) =>
    api.patch(`/users/${id}/reset-password`, passwordData),

  adminResetPassword: (id: string, passwordData: { newPassword: string; resetType?: string }) =>
    api.patch(`/users/${id}/admin-reset-password`, passwordData),

  deleteUser: (id: string) =>
    api.delete(`/users/${id}`),

  softDeleteUser: (id: string) =>
    api.post(`/users/${id}/soft-delete`),

  restoreUser: (id: string, status: string) =>
    api.post(`/users/${id}/restore`, { status }),

  placeMembershipOnHold: (id: string, holdData: any) =>
    api.put(`/member-profiles/${id}/hold`, holdData),

  activateMembership: (id: string) =>
    api.put(`/member-profiles/${id}/activate`),

  getUserMemberships: (userId: string) =>
    api.get(`/member-profiles/user/${userId}`),
};

// Tenant API (for super admin only)
export const tenantAPI = {
  getAllTenants: () =>
    api.get('/tenants'),

  getTenants: () =>
    api.get('/tenants'),

  getTenantById: (id: string) =>
    api.get(`/tenants/${id}`),

  createTenant: (tenantData: any) =>
    api.post('/tenants', tenantData),

  updateTenant: (id: string, tenantData: any) =>
    api.put(`/tenants/${id}`, tenantData),

  deleteTenant: (id: string) =>
    api.delete(`/tenants/${id}`),

  getTenantStats: (id: string) =>
    api.get(`/tenants/${id}/stats`),
};

// Tenant Settings API
export const tenantSettingsAPI = {

  getPublicSettings: (tenantName: string) =>
    api.get('/tenant-settings/public', { headers: { tenantName } }),

  getAllSettings: () =>
    api.get('/tenant-settings'),

  getSetting: (key: string) =>
    api.get(`/tenant-settings/${key}`),

  updateSetting: (key: string, value: any) =>
    api.put(`/tenant-settings/${key}`, { value }),

  updateMultipleSettings: (settings: Record<string, any>) =>
    api.post('/tenant-settings', settings),
};

// Court booking no-show API (staff only)
export const noShowAPI = {
  getBookingAttendance: (amenityId: string, bookingId: string) =>
    api.get(`/amenities/${amenityId}/bookings/${bookingId}/attendance`),

  getMemberNoShows: (memberProfileId: string) =>
    api.get(`/member-profiles/${memberProfileId}/no-shows`),
};

// Member Profile API
export const memberProfileAPI = {
  // Staff only. Member-facing screens must use searchMemberProfiles.
  getAllMemberProfiles: (filters?: any) =>
    api.get('/member-profiles', { params: filters }),

  // Minimal, paged member search (id / name / membership number / tier) for pickers on member-facing screens
  searchMemberProfiles: (q: string, limit: number = 20, offset: number = 0) =>
    api.get('/member-profiles/search', { params: { q, limit, offset } }),

  getMemberProfileCount: (filters?: any) =>
    api.get('/member-profiles/count', { params: filters }),
  
  getMemberProfileById: (id: string) =>
    api.get(`/member-profiles/${id}`),

  getUserMemberProfiles: (userId: string) =>
    api.get(`/member-profiles/user/${userId}`),

  getMemberProfilesByEmail: (email: string) =>
    api.get(`/member-profiles/email/${email}`),

  createMemberProfile: (profileData: any) =>
    api.post('/member-profiles', profileData),

  updateMemberProfile: (id: string, profileData: any) =>
    api.put(`/member-profiles/${id}`, profileData),

  updateMemberProfileBasicInfo: (id: string, profileData: { firstName: string; lastName: string; email: string; phone: string; emailConfirmation?: string }) =>
    api.put(`/member-profiles/${id}/basic-info`, profileData),

  requestMembershipChange: (id: string, changeData: { membershipTierId: string; membershipChangeType: string; proratedCharge?: number; effectiveDate?: string }) =>
    api.put(`/member-profiles/${id}/change-membership`, changeData),

  getScheduledMembershipChange: (id: string) =>
    api.get(`/member-profiles/${id}/scheduled-change`),

  cancelScheduledMembershipChange: (id: string, scheduledChangeId: string) =>
    api.post(`/member-profiles/${id}/cancel-scheduled-change`, { scheduledChangeId }),

  recordProfileAccess: (id: string) =>
    api.put(`/member-profiles/${id}/record-access`),

  deleteMemberProfile: (id: string) =>
    api.delete(`/member-profiles/${id}`),

  softDeleteMemberProfile: (id: string) =>
    api.post(`/member-profiles/${id}/soft-delete`),

  restoreMemberProfile: (id: string, status: string) =>
    api.post(`/member-profiles/${id}/restore`, { status }),

  placeMemberProfileOnHold: (id: string, holdData: any) =>
    api.put(`/member-profiles/${id}/hold`, holdData),

  activateMemberProfile: (id: string) =>
    api.put(`/member-profiles/${id}/activate`),

  // Onboarding APIs
  getOnboardingStatus: (id: string) =>
    api.get(`/member-profiles/${id}/onboarding-status`),

  updateOnboardingStep: (id: string, stepUpdate: { step: string; completed: boolean; notes?: string }) =>
    api.put(`/member-profiles/${id}/onboarding-step`, stepUpdate),

  updateOnboardingStatus: (id: string, onboardingStatus: any) =>
    api.put(`/member-profiles/${id}/onboarding-status`, { onboardingStatus }),

  // Profile picture APIs
  getProfilePicture: (id: string) =>
    api.get(`/member-profiles/${id}/profile-picture`, {
      responseType: 'blob',
    }),

  uploadProfilePicture: (id: string, file: File) => {
    const formData = new FormData();
    formData.append('profilePicture', file);
    return api.post(`/member-profiles/${id}/profile-picture`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  deleteProfilePicture: (id: string) =>
    api.delete(`/member-profiles/${id}/profile-picture`),

  // QR code API
  getMemberProfileQRCode: (id: string) =>
    api.get(`/member-profiles/${id}/qr-code`, {
      responseType: 'blob',
    }),

  decryptQRCode: (encryptedData: string) =>
    api.post('/member-profiles/qr-decrypt', { encryptedData }),

  // Booking history (paginated)
  getMemberAmenityBookings: (id: string, params?: { page?: number; limit?: number }) =>
    api.get(`/member-profiles/${id}/bookings/amenity`, { params }),

  getMemberGroupPlayBookings: (id: string, params?: { page?: number; limit?: number }) =>
    api.get(`/member-profiles/${id}/bookings/group-play`, { params }),

  getMemberClassBookings: (id: string, params?: { page?: number; limit?: number }) =>
    api.get(`/member-profiles/${id}/bookings/class`, { params }),

  getMemberCoachingBookings: (id: string, params?: { page?: number; limit?: number }) =>
    api.get(`/member-profiles/${id}/bookings/coaching`, { params }),
};

// Membership Tier API
export const membershipTierAPI = {
  getMembershipTiers: (showAll = false) =>
    api.get('/membership-tiers', { params: { showAll } }),

  getMembershipTierById: (id: string) =>
    api.get(`/membership-tiers/${id}`),

  createMembershipTier: (tierData: any) =>
    api.post('/membership-tiers', tierData),

  updateMembershipTier: (id: string, tierData: any) =>
    api.put(`/membership-tiers/${id}`, tierData),

  deleteMembershipTier: (id: string) =>
    api.delete(`/membership-tiers/${id}`),
};

// Amenity API
export const amenityAPI = {
  getAmenities: () =>
    api.get('/amenities'),

  getAmenityById: (id: string) =>
    api.get(`/amenities/${id}`),

  createAmenity: (amenityData: any) =>
    api.post('/amenities', amenityData),

  updateAmenity: (id: string, amenityData: any) =>
    api.put(`/amenities/${id}`, amenityData),

  deleteAmenity: (id: string) =>
    api.delete(`/amenities/${id}`),
    
  getAmenityBookings: (amenityId: string) =>
    api.get(`/amenities/${amenityId}/bookings`),

  // Calculate fee breakdown for a booking (without creating it)
  calculateAmenityBookingFees: (amenityId: string, bookingData: any) =>
    api.post(`/amenities/${amenityId}/calculate-fees`, bookingData),

  createAmenityBooking: (amenityId: string, bookingData: any) =>
    api.post(`/amenities/v2/${amenityId}/bookings`, bookingData),

  getAmenityBookingById: (amenityId: string, bookingId: string) =>
    api.get(`/amenities/${amenityId}/bookings/${bookingId}`),

  updateAmenityBooking: (amenityId: string, bookingId: string, bookingData: any) =>
    api.put(`/amenities/${amenityId}/bookings/${bookingId}`, bookingData),

  cancelAmenityBooking: (amenityId: string, bookingId: string, data?: any) =>
    api.delete(`/amenities/${amenityId}/bookings/${bookingId}`, { data }),

  checkAmenityAvailability: (amenityId: string, date: string) =>
    api.get(`/amenities/${amenityId}/availability`, { params: { date } }),

  // New method to get total amenity booking count
  getAllAmenityBookings: (filters?: any) =>
    api.get('/amenities/bookings/all', { params: filters }),

  // Create reservation (booking with 'on_hold' status)
  createReservation: (amenityId: string, reservationData: any) =>
    api.post(`/amenities/${amenityId}/reservations`, reservationData),

  // Get current user's amenity bookings
  getUserBookings: () =>
    api.get('/amenities/my-bookings'),

  // Get current coach/staff reservations
  getCoachReservations: () =>
    api.get('/amenities/my-reservations'),

  // Schedule maintenance for amenities
  scheduleMaintenance: (maintenanceData: {
    date: string;
    startTime: string;
    endTime: string;
    reason: string;
    amenities: string[];
  }) =>
    api.post('/amenities/schedule-maintenance', maintenanceData),

  // Get max booking date for a member's tier
  getMaxBookingDate: (amenityId: string, memberProfileId: string) =>
    api.get(`/amenities/${amenityId}/max-booking-date`, { params: { memberProfileId } }),

  // Check for missing amenity bookings
  checkMissingBookings: (instances: Array<{
    id: string;
    type: 'coachingSession' | 'groupPlayInstance' | 'coachingProgramInstance';
    venues: string[];
    sessionDate: string;
    startTime: string;
    endTime: string;
  }>, signal?: AbortSignal) =>
    api.post('/amenities/check-missing-bookings', { instances }, { signal }),
};

// Amenity Type API
export const amenityTypeAPI = {
  getAll: () =>
    api.get('/amenity-types'),

  getById: (id: string) =>
    api.get(`/amenity-types/${id}`),

  create: (amenityTypeData: any) =>
    api.post('/amenity-types', amenityTypeData),

  update: (id: string, amenityTypeData: any) =>
    api.put(`/amenity-types/${id}`, amenityTypeData),

  delete: (id: string) =>
    api.delete(`/amenity-types/${id}`),
};

// Equipment API
export const equipmentAPI = {
  getAll: (activeOnly?: boolean) =>
    api.get('/equipment', { params: activeOnly ? { activeOnly: 'true' } : {} }),

  getById: (id: string) =>
    api.get(`/equipment/${id}`),

  getForAmenity: (amenityId: string) =>
    api.get(`/equipment/amenity/${amenityId}`),

  getAmenitiesForEquipment: (equipmentId: string) =>
    api.get(`/equipment/${equipmentId}/amenities`),

  create: (equipmentData: {
    name: string;
    description?: string;
    quantity: number;
    active?: boolean;
    costAmount: number;
    costUnit: number;
    maxPerBooking?: number;
  }) =>
    api.post('/equipment', equipmentData),

  update: (id: string, equipmentData: {
    name?: string;
    description?: string;
    quantity?: number;
    active?: boolean;
    costAmount?: number;
    costUnit?: number;
    maxPerBooking?: number;
  }) =>
    api.put(`/equipment/${id}`, equipmentData),

  delete: (id: string) =>
    api.delete(`/equipment/${id}`),

  associateWithAmenity: (equipmentId: string, amenityId: string) =>
    api.post(`/equipment/${equipmentId}/amenities/${amenityId}`),

  disassociateFromAmenity: (equipmentId: string, amenityId: string) =>
    api.delete(`/equipment/${equipmentId}/amenities/${amenityId}`),

  checkAvailability: (equipmentId: string, bookingDate: string, startTime: string, endTime: string) =>
    api.get(`/equipment/${equipmentId}/availability`, {
      params: { bookingDate, startTime, endTime }
    }),
};

// Amenity Schedule API
export const amenityScheduleAPI = {
  getAmenitySchedules: (amenityId: string) =>
    api.get(`/amenity-schedules/${amenityId}`),

  getScheduleById: (scheduleId: string) =>
    api.get(`/amenity-schedules/schedule/${scheduleId}`),

  getActiveScheduleForBooking: (amenityId: string, date: string, startTime?: string, endTime?: string) =>
    api.get(`/amenity-schedules/${amenityId}/active-schedule`, { 
      params: { date, startTime, endTime } 
    }),

  createSchedule: (amenityId: string, scheduleData: any) =>
    api.post(`/amenity-schedules/${amenityId}`, scheduleData),

  updateSchedule: (scheduleId: string, scheduleData: any) =>
    api.put(`/amenity-schedules/schedule/${scheduleId}`, scheduleData),

  deleteSchedule: (scheduleId: string) =>
    api.delete(`/amenity-schedules/schedule/${scheduleId}`),

  getAvailableSlots: (amenityId: string, date: string, memberProfileId?: string) =>
    api.get(`/amenity-schedules/${amenityId}/slots`, { params: { date, memberProfileId } }),

  getMinBookingDuration: (amenityId: string) =>
    api.get(`/amenity-schedules/${amenityId}/min-duration`),
};

// Membership Application API
export const membershipApplicationAPI = {
  getApplications: (filters?: any) =>
    api.get('/membership-applications', { params: filters }),
    
  getPendingApplications: () =>
    api.get('/membership-applications/pending'),
    
  getPendingCount: () =>
    api.get('/membership-applications/pending/count'),
    
  getApplicationById: (id: string) =>
    api.get(`/membership-applications/${id}`),

  // TODO: DELETE
  // approveApplication: (id: string) =>
  //   api.put(`/membership-applications/${id}/approve`),
    
  rejectApplication: (id: string, reason: string) =>
    api.put(`/membership-applications/${id}/reject`, { rejectionReason: reason }),
    
  resendApplicationEmail: (id: string) =>
    api.post(`/membership-applications/${id}/resend-email`),
    
  deleteApplication: (id: string) =>
    api.delete(`/membership-applications/${id}`),
    
  createUserFromApplication: (userData: any) =>
    api.post('/users', userData),
};

// Class API
export const classAPI = {
  getClasses: (filters?: any) =>
    api.get('/classes', { params: filters }),

  getClassById: (id: string) =>
    api.get(`/classes/${id}`),

  // Get class instances (scheduled occurrences)
  getClassInstances: (filters?: any) =>
    api.get('/class-instances', { params: filters }),

  createClass: (classData: any) =>
    api.post('/classes', classData),

  updateClass: (id: string, classData: any) =>
    api.put(`/classes/${id}`, classData),

  deleteClass: (id: string) =>
    api.delete(`/classes/${id}`),

  getClassBookings: (classId: string) =>
    api.get(`/classes/${classId}/bookings`),

  createClassBooking: (classId: string, bookingData?: { notes?: string }) =>
    api.post(`/classes/${classId}/bookings`, bookingData || {}),

  cancelClassBooking: (classId: string, bookingId: string) =>
    api.delete(`/classes/${classId}/bookings/${bookingId}`),

  // Admin functionality to register a member to a class
  registerMemberToClass: (classId: string, memberData: { memberProfileId: string, notes?: string }) =>
    api.post(`/classes/${classId}/bookings/admin`, memberData),

  // Admin functionality to cancel a member's class booking
  cancelMemberBooking: (classId: string, bookingId: string, cancelTransaction?: boolean) =>
    api.delete(`/classes/${classId}/bookings/${bookingId}/admin`, { 
      data: { cancelTransaction } 
    }),

  recordClassAttendance: (classId: string, attendanceData: any) =>
    api.post(`/classes/${classId}/attendance`, attendanceData),

  getClassSchedule: (filters?: any) =>
    api.get('/classes/schedule', { params: filters }),

  addToWaitlist: (classId: string) =>
    api.post(`/classes/${classId}/waitlist`),

  removeFromWaitlist: (classId: string, userId: string) =>
    api.delete(`/classes/${classId}/waitlist/${userId}`),
  
  getWaitlist: (classId: string) =>
    api.get(`/classes/${classId}/waitlist`),

  // New method to get class count for a specific date range
  getClassCount: (startDate?: string, endDate?: string) =>
    api.get('/classes', { params: { startDate, endDate } }),

  // Get current user's class bookings
  getUserBookings: () =>
    api.get('/classes/my-bookings'),
  
  // Get cancellation policy for a booking
  getCancellationPolicyForBooking: (classId: string, bookingId: string) =>
    api.get(`/classes/${classId}/bookings/${bookingId}/cancellation-policy`),
};

// Class Type API
export const classTypeAPI = {
  getAll: () =>
    api.get('/class-types'),

  getById: (id: string) =>
    api.get(`/class-types/${id}`),

  create: (classTypeData: { name: string; description?: string; color?: string }) =>
    api.post('/class-types', classTypeData),

  update: (id: string, classTypeData: { name?: string; description?: string; color?: string; active?: boolean }) =>
    api.put(`/class-types/${id}`, classTypeData),

  delete: (id: string) =>
    api.delete(`/class-types/${id}`)
};

// Class Level API
export const classLevelAPI = {
  getAll: () =>
    api.get('/class-levels'),

  getById: (id: string) =>
    api.get(`/class-levels/${id}`),

  create: (classLevelData: { name: string; description?: string; sortOrder?: number }) =>
    api.post('/class-levels', classLevelData),

  update: (id: string, classLevelData: { name?: string; description?: string; sortOrder?: number; active?: boolean }) =>
    api.put(`/class-levels/${id}`, classLevelData),

  delete: (id: string) =>
    api.delete(`/class-levels/${id}`)
};

export const discountAPI = {
  getAll: () => 
    api.get('/discounts'),
  
  getById: (id: string) => 
    api.get(`/discounts/${id}`),
  
  create: (discountData: {
    name: string;
    type: 'one time' | 'ongoing';
    category: 'percentage' | 'amount';
    value: number;
    description?: string;
  }) => 
    api.post('/discounts', discountData),
  
  update: (id: string, discountData: {
    name?: string;
    type?: 'one time' | 'ongoing';
    category?: 'percentage' | 'amount';
    value?: number;
    description?: string;
    isActive?: boolean;
  }) => 
    api.put(`/discounts/${id}`, discountData),
  
  delete: (id: string) => 
    api.delete(`/discounts/${id}`),
};

export const oneTimeFeeAPI = {
  getAll: () => 
    api.get('/one-time-fees'),
  
  getById: (id: number) => 
    api.get(`/one-time-fees/${id}`),
  
  getForUser: (userId: string) => 
    api.get(`/users/${userId}/one-time-fees`),
  
  create: (feeData: {
    userId?: string;
    feeType: 'initiation' | 'activation' | 'equipment' | 'locker_rental' | 'personal_training' | 'late_fee' | 'cancellation' | 'other';
    feeName: string;
    amount: number;
    dueDate?: string;
  }) => 
    api.post('/one-time-fees', feeData),
  
  update: (id: number, feeData: {
    feeType?: 'initiation' | 'activation' | 'equipment' | 'locker_rental' | 'personal_training' | 'late_fee' | 'cancellation' | 'other';
    feeName?: string;
    amount?: number;
    status?: 'pending' | 'paid' | 'waived' | 'cancelled';
    waivedReason?: string;
    dueDate?: string;
  }) => 
    api.put(`/one-time-fees/${id}`, feeData),
  
  delete: (id: number) => 
    api.delete(`/one-time-fees/${id}`),
};

export const memberGroupAPI = {
  getAll: () => 
    api.get('/member-groups'),
  
  getById: (id: number) => 
    api.get(`/member-groups/${id}`),
  
  create: (groupData: {
    groupName: string;
    groupType: 'family' | 'corporate';
    primaryContactId?: string | null;
    companyName?: string;
    billingAddress?: string;
    groupDiscountPercent?: number;
    maxMembers?: number;
    chargeToGroupPrimaryContact?: boolean;
  }) => 
    api.post('/member-groups', groupData),
  
  update: (id: number, groupData: {
    groupName?: string;
    groupType?: 'family' | 'corporate';
    primaryContactId?: string | null;
    companyName?: string;
    billingAddress?: string;
    groupDiscountPercent?: number;
    maxMembers?: number;
    isActive?: boolean;
    chargeToGroupPrimaryContact?: boolean;
  }) => 
    api.put(`/member-groups/${id}`, groupData),

  updateWithMembers: (id: number, groupData: {
    groupName?: string;
    groupType?: 'family' | 'corporate';
    primaryContactId?: string | null;
    companyName?: string;
    billingAddress?: string;
    groupDiscountPercent?: number;
    maxMembers?: number;
    isActive?: boolean;
    chargeToGroupPrimaryContact?: boolean;
    memberProfileIds?: string[];
  }) => 
    api.put(`/member-groups/${id}/with-members`, groupData),
  
  delete: (id: number) => 
    api.delete(`/member-groups/${id}`),
  
  getMembers: (id: number) => 
    api.get(`/member-groups/${id}/members`),
  
  addMember: (groupId: number, userId: string, role: 'primary' | 'secondary' | 'dependent') => 
    api.post(`/member-groups/${groupId}/members`, { userId, role }),
  
  removeMember: (groupId: number, userId: string) => 
    api.delete(`/member-groups/${groupId}/members/${userId}`),

  applyPaymentToGroup: (groupId: number, applyToGroup: boolean, paymentInfo?: any) =>
    api.post(`/member-groups/${groupId}/apply-payment`, { applyToGroup, paymentInfo }),

  removeGroupPayment: (groupId: number) =>
    api.delete(`/member-groups/${groupId}/payment`),

  checkPendingTransactions: (groupId: number, checkGroupBilled?: boolean) =>
    api.get(`/member-groups/${groupId}/pending-transactions${checkGroupBilled ? '?checkGroupBilled=true' : ''}`),

  reassignPendingTransactions: (groupId: number) =>
    api.post(`/member-groups/${groupId}/reassign-transactions`),

  reassignPendingTransactionsToIndividuals: (groupId: number) =>
    api.post(`/member-groups/${groupId}/reassign-to-individuals`),

  checkPendingTransactionsForMembers: (groupId: number, memberIds: string[], checkGroupBilled?: boolean) =>
    api.post(`/member-groups/${groupId}/check-member-transactions`, { memberIds, checkGroupBilled }),

  reassignPendingTransactionsForMembers: (groupId: number, memberIds: string[], assignToGroup: boolean) =>
    api.post(`/member-groups/${groupId}/reassign-member-transactions`, { memberIds, assignToGroup }),
};

export const auditLogAPI = {
  getAuditLogs: (params?: {
    page?: number;
    limit?: number;
    userId?: string;
    entityType?: string;
    entityId?: string;
    action?: string;
    severity?: string;
    category?: string;
    startDate?: string;
    endDate?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          searchParams.append(key, String(value));
        }
      });
    }
    return api.get(`/audit-logs?${searchParams.toString()}`);
  },
  
  getEntityAuditLogs: (entityType: string, entityId: string, params?: {
    page?: number;
    limit?: number;
  }) => {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
    }
    return api.get(`/audit-logs/entity/${entityType}/${entityId}?${searchParams.toString()}`);
  },
  
  getAuditLogById: (id: string) => 
    api.get(`/audit-logs/${id}`),
};

// Events API
export const eventAPI = {
  // Get all events (with optional filters)
  getAll: (params?: {
    status?: string;
    upcoming?: boolean;
    includeRegistrations?: boolean;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.upcoming !== undefined) queryParams.append('upcoming', params.upcoming.toString());
    if (params?.includeRegistrations !== undefined) queryParams.append('includeRegistrations', params.includeRegistrations.toString());
    
    const query = queryParams.toString();
    return api.get(`/events${query ? `?${query}` : ''}`);
  },

  // Get event by ID
  getById: (id: string) => 
    api.get(`/events/${id}`),

  // Create new event (Admin only)
  create: (eventData: {
    title: string;
    description: string;
    category?: string;
    eventDateTime: string;
    cost: number;
    maxRegistrants?: number;
    registrationStartDate?: string;
    registrationDeadline?: string;
    allowRegistrationUntilStart: boolean;
    waitlistEnabled: boolean;
    location?: string;
    notes?: string;
    status: string;
  }) => 
    api.post('/events', eventData),

  // Update event (Admin only)
  update: (id: string, eventData: {
    title?: string;
    description?: string;
    category?: string;
    eventDateTime?: string;
    cost?: number;
    maxRegistrants?: number;
    registrationStartDate?: string;
    registrationDeadline?: string;
    allowRegistrationUntilStart?: boolean;
    waitlistEnabled?: boolean;
    location?: string;
    notes?: string;
    status?: string;
  }) => 
    api.put(`/events/${id}`, eventData),

  // Delete event (Admin only)
  delete: (id: string) => 
    api.delete(`/events/${id}`),

  // Register for event (Member)
  register: (eventId: string, registrationData: {
    memberProfileId: string;
    notes?: string;
    guests?: {
      name: string;
      email: string;
      phone: string;
    }[];
  }) => 
    api.post(`/events/${eventId}/register`, registrationData),

  // Cancel registration
  cancelRegistration: (registrationId: string, reason?: string) => 
    api.delete(`/events/registrations/${registrationId}`, { 
      data: { reason }
    }),

  // Get user's registrations
  getUserRegistrations: (userId: string) => 
    api.get(`/events/users/${userId}/registrations`),

  // Get event registrations (Admin only)
  getEventRegistrations: (eventId: string) => 
    api.get(`/events/${eventId}/registrations`),
};

// Transaction API
export const transactionAPI = {
  // Get all transactions with filtering
  getAll: (filters?: {
    userId?: string;
    type?: string;
    status?: string;
    /** Only unresolved Shop / F&B member-account charges flagged "needs review" */
    needsReview?: boolean;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
    search?: string;
  }) =>
    api.get('/transactions', { params: filters }),

  // Get a specific transaction by ID
  getById: (id: string) => 
    api.get(`/transactions/${id}`),

  // Create a new transaction (Super Admin only)
  create: (data: {
    memberProfileId: string;
    type: string;
    amount: number;
    tax?: number;
    description?: string;
    transactionDate?: string;
    status?: string;
    discountPercentage?: number;
    discountAmount?: number;
    relatedEntityId?: string;
    relatedEntityType?: string;
    metadata?: any;
  }) =>
    api.post('/transactions', data),

  // Create a one-time fee transaction
  createOneTimeFeeTransaction: (data: {
    memberProfileId: string;
    oneTimeFeeId: number;
    description?: string;
    paymentMethod?: string;
    paymentId?: string;
  }) => 
    api.post('/transactions/one-time-fee', data),

  // Update a transaction (Admin only)
  update: (id: string, data: {
    status?: string;
    paymentMethod?: string;
    paymentId?: string;
    description?: string;
    metadata?: any;
    adminNote?: string;
  }) => 
    api.put(`/transactions/${id}`, data),

  // Delete a transaction (Super Admin only)
  delete: (id: string) => 
    api.delete(`/transactions/${id}`),

  // Get transactions for a specific member profile
  getMemberProfileTransactions: (memberProfileId: string, filters?: {
    type?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }) => 
    api.get(`/transactions/member-profile/${memberProfileId}`, { params: filters }),

  // Get transactions for the current logged-in member
  getMyTransactions: (filters?: {
    type?: string;
    status?: string;
    limit?: number;
    offset?: number;
    startDate?: string;
    endDate?: string;
  }) => 
    api.get('/transactions/my-transactions', { params: filters }),

  // Get transactions for all members in a group (for primary contact)
  getGroupTransactions: (groupId: string, filters?: {
    type?: string;
    status?: string;
    limit?: number;
    offset?: number;
    startDate?: string;
    endDate?: string;
  }) => 
    api.get(`/transactions/group/${groupId}`, { params: filters }),

  // Apply or remove discount from pending transactions
  applyDiscountToPendingTransactions: (memberProfileId: string, data: {
    discountAction: 'add' | 'remove';
    transactionIds: string[];
  }) =>
    api.post(`/transactions/member-profile/${memberProfileId}/apply-discount`, data),

  // Refund a completed transaction (Admin/Super Admin only)
  refund: (id: string, data: {
    adminNote: string;
    amount?: number; // Optional partial refund amount
  }) =>
    api.post(`/transactions/${id}/refund`, data),

  // Refund a completed transaction as a wallet credit instead of via payment processor
  refundAsWalletCredit: (id: string, data: { adminNote: string }) =>
    api.post(`/transactions/${id}/refund-as-wallet-credit`, data),

  // Mark a pending/failed transaction as manually paid — Super Admin only
  markAsPaid: (id: string, data: { settlementNote: string }) =>
    api.post(`/transactions/${id}/mark-paid`, data),

  // Write off a pending/failed transaction — Admin/Super Admin only
  writeOff: (id: string, data: { writeOffNote: string }) =>
    api.post(`/transactions/${id}/write-off`, data),

  // Resolve an unresolved ("needs review") Shop / F&B member-account charge — Admin/Super Admin only.
  // 'completed' = the member really was charged; 'failed' = they were not. Optional helcimTransactionId
  // (with 'completed') lets the charge be refunded later.
  resolveCharge: (id: string, data: { outcome: 'completed' | 'failed'; adminNote: string; helcimTransactionId?: string }) =>
    api.post(`/transactions/${id}/resolve-charge`, data),
};

// Payment Methods API
export const paymentMethodAPI = {

  // Initialize the payment processor 
  initializePaymentProcessor: () =>
    api.get('/payment-processor/initialize'),
  
  // Get payment methods for current user
  getMyPaymentMethods: () => 
    api.get('/payment-processor/payment-methods'),

  // Get payment methods for a specific member profile (admin)
  getMemberPaymentMethods: (memberProfileId: string) =>
    api.get(`/member-profiles/${memberProfileId}/payment-methods`),

  // Add a new payment method (legacy form-based)
  addPaymentMethod: (data: {
    cardNumber: string;
    expiryMonth: string;
    expiryYear: string;
    cvv: string;
    cardholderName: string;
    billingAddress: {
      street: string;
      city: string;
      state: string;
      zipCode: string;
      country: string;
    };
  }) => 
    api.post('/payment-processor/add-payment-method', data),

  // Add payment method using HelcimPay token
  addPaymentMethodToken: (data: {
    cardToken: string;
    customerId?: string;
    cardNumber?: any;
    verificationMessage?: string;
    memberProfileId: string;
  }) => 
    api.post('/payment-processor/add-payment-method-token', data),

  // Delete a payment method
  deletePaymentMethod: (id: string, memberProfileId?: string) => 
    api.delete(`/payment-processor/payment-methods/${id}`, {
      headers: memberProfileId ? { 'X-Member-Profile-ID': memberProfileId } : {}
    }),

  // Set default payment method
  setDefaultPaymentMethod: (id: string, memberProfileId?: string) => 
    api.patch(`/payment-processor/payment-methods/${id}/set-default`, {}, {
      headers: memberProfileId ? { 'X-Member-Profile-ID': memberProfileId } : {}
    }),

  // Process a single transaction manually (SUPER_ADMIN only)
  processTransaction: (transactionId: string) =>
    api.post('/payment-processor/process-transaction', { transactionId }),
  
  // Get invoice for completed transaction
  getInvoice: (transactionId: string) =>
    api.get(`/payment-processor/invoice/${transactionId}`),
};

// Dropdown Management API
export const dropdownManagementAPI = {
  // Class Type Management
  getClassTypeUsage: (id: string) =>
    api.get(`/dropdown-management/class-types/${id}/usage`),
  
  deleteClassType: (id: string) =>
    api.delete(`/dropdown-management/class-types/${id}`),

  // Class Level Management
  getClassLevelUsage: (id: string) =>
    api.get(`/dropdown-management/class-levels/${id}/usage`),
  
  deleteClassLevel: (id: string) =>
    api.delete(`/dropdown-management/class-levels/${id}`),

  // Amenity Type Management
  getAmenityTypeUsage: (id: string) =>
    api.get(`/dropdown-management/amenity-types/${id}/usage`),
  
  deleteAmenityType: (id: string) =>
    api.delete(`/dropdown-management/amenity-types/${id}`),
};

// Coaching Session API
export const coachingSessionAPI = {
  // Validate coaching time slots
  validateTimeSlots: (data: {
    venueIds: string[];
    sessionDate: string;
    startTime: string;
    endTime: string;
  }) => api.post('/coaching-sessions/validate-time-slots', data),

  // Get all coaching sessions
  getCoachingSessions: (params?: {
    coachId?: string;
    level?: string;
    venue?: string;
    startDate?: string;
    endDate?: string;
  }) => api.get('/coaching-sessions', { params }),

  // Get coaching session by ID
  getCoachingSessionById: (sessionId: string) =>
    api.get(`/coaching-sessions/${sessionId}`),

  // Create coaching session
  createCoachingSession: (data: any) =>
    api.post('/coaching-sessions', data),

  // Update coaching session
  updateCoachingSession: (sessionId: string, data: any) =>
    api.put(`/coaching-sessions/${sessionId}`, data),

  // Delete coaching session
  deleteCoachingSession: (sessionId: string) =>
    api.delete(`/coaching-sessions/${sessionId}`),

  // Get coaching session availability
  getCoachingSessionAvailability: (sessionId: string) =>
    api.get(`/coaching-sessions/${sessionId}/availability`),

  // Book a coaching session
  bookCoachingSession: (sessionId: string, data: any) =>
    api.post(`/coaching-sessions/${sessionId}/book`, data),

  // Get coaching session bookings
  getCoachingSessionBookings: (sessionId: string) =>
    api.get(`/coaching-sessions/${sessionId}/bookings`),

  // Cancel coaching session booking
  cancelCoachingSessionBooking: (sessionId: string, bookingId: string) =>
    api.delete(`/coaching-sessions/${sessionId}/bookings/${bookingId}`),

  // Get current user's coaching session bookings
  getUserBookings: () =>
    api.get('/coaching-sessions/my-bookings'),
};

// Coaching Program API
export const coachingProgramAPI = {
  // Get all coaching programs
  getCoachingPrograms: (params?: {
    coachId?: string;
    level?: string;
    venue?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
  }) => api.get('/coaching-programs', { params }),

  // Get coaching program by ID
  getCoachingProgramById: (programId: string) =>
    api.get(`/coaching-programs/${programId}`),

  // Create coaching program
  createCoachingProgram: (data: any) =>
    api.post('/coaching-programs', data),

  // Update coaching program
  updateCoachingProgram: (programId: string, data: any) =>
    api.put(`/coaching-programs/${programId}`, data),

  // Delete coaching program
  deleteCoachingProgram: (programId: string) =>
    api.delete(`/coaching-programs/${programId}`),

  // Get coaching program enrollments
  getCoachingProgramEnrollments: (programId: string) =>
    api.get(`/coaching-programs/${programId}/enrollments`),

  // Get coaching program instances (for drop-in enrollment)
  getProgramInstances: (programId: string, params?: { startDate?: string; endDate?: string; includeEnrollments?: string }) =>
    api.get(`/coaching-programs/${programId}/instances`, { params }),

  // Enroll in coaching program
  enrollInCoachingProgram: (programId: string, data: any) =>
    api.post(`/coaching-programs/${programId}/enroll`, data),

  // Cancel enrollment
  cancelEnrollment: (programId: string, enrollmentId: string) =>
    api.delete(`/coaching-programs/${programId}/enrollments/${enrollmentId}`),

  // Member self-cancellation of their own enrollment
  cancelOwnEnrollment: (programId: string) =>
    api.post(`/coaching-programs/${programId}/cancel-own-enrollment`),

  // Update enrollment status
  updateEnrollmentStatus: (programId: string, enrollmentId: string, status: string) =>
    api.put(`/coaching-programs/${programId}/enrollments/${enrollmentId}`, { status }),

  // Get current user's program enrollments
  getUserEnrollments: () =>
    api.get('/coaching-programs/my-enrollments'),
};

// Coach API
export const coachAPI = {
  // Get recent activity for a coach
  getCoachActivity: (coachId: string, params?: { limit?: number }) =>
    api.get(`/coaches/${coachId}/activity`, { params }),

  // Get coach schedules
  getCoachSchedules: (params?: { coachId?: string }) =>
    api.get('/coaches/schedules', { params }),

  // Get blocked times for a coach
  getCoachBlockedTimes: (coachId: string) =>
    api.get(`/coaches/${coachId}/blocked-times`),
};

// Class Instance API
export const classInstanceAPI = {
  getInstances: (params?: any) =>
    api.get('/class-instances', { params }),

  getInstance: (id: string) =>
    api.get(`/class-instances/${id}`),

  createInstance: (instanceData: any) =>
    api.post('/class-instances', instanceData),

  updateInstance: (id: string, instanceData: any) =>
    api.put(`/class-instances/${id}`, instanceData),

  deleteInstance: (id: string) =>
    api.delete(`/class-instances/${id}`),

  generateInstances: (classId: string, data: any) =>
    api.post(`/class-instances/generate/${classId}`, data),

  // Admin route - register any member to a class instance
  registerMemberToInstance: (instanceId: string, data: any) =>
    api.post(`/class-instances/${instanceId}/register`, data),

  // Member route - book yourself into a class instance
  bookInstance: (instanceId: string, data: any) =>
    api.post(`/class-instances/${instanceId}/book`, data),

  unregisterMemberFromInstance: (instanceId: string, bookingId: string) =>
    api.delete(`/class-instances/${instanceId}/bookings/${bookingId}`),

  // Member route to cancel their own booking
  cancelOwnBooking: (instanceId: string, bookingId: string) =>
    api.delete(`/class-instances/${instanceId}/bookings/${bookingId}/cancel`),

  // Member route to get cancellation policy
  getCancellationPolicyForInstanceBooking: (instanceId: string, bookingId: string) =>
    api.get(`/class-instances/${instanceId}/bookings/${bookingId}/cancellation-policy`),

  cancelInstance: (instanceId: string) =>
    api.post(`/class-instances/${instanceId}/cancel`),
};

// Coaching Program Instance API
export const coachingProgramInstanceAPI = {
  getInstances: (params?: any) =>
    api.get('/coaching-program-instances', { params }),

  getInstance: (id: string) =>
    api.get(`/coaching-program-instances/${id}`),

  // Get current user's instance enrollments (drop-in bookings)
  getUserEnrollments: () =>
    api.get('/coaching-program-instances/my-enrollments'),

  createInstance: (instanceData: any) =>
    api.post('/coaching-program-instances', instanceData),

  updateInstance: (id: string, instanceData: any) =>
    api.put(`/coaching-program-instances/${id}`, instanceData),

  deleteInstance: (id: string) =>
    api.delete(`/coaching-program-instances/${id}`),

  generateInstances: (programId: string, data: any) =>
    api.post(`/coaching-program-instances/generate/${programId}`, data),

  // Cancel instance enrollment (for drop-in enrollments)
  cancelInstanceEnrollment: (instanceId: string, enrollmentId: string) =>
    api.delete(`/coaching-program-instances/${instanceId}/enrollments/${enrollmentId}`),

  // Cancel entire instance (admin/coach only)
  cancelInstance: (instanceId: string) =>
    api.post(`/coaching-program-instances/${instanceId}/cancel`),
};

// Document Review API
export const documentReviewAPI = {
  // Settings
  getSettings: () =>
    api.get('/document-review/settings'),

  updateSettings: (settings: { enabled: boolean; reviewPeriodDays: number }) =>
    api.put('/document-review/settings', settings),

  // Document Templates (Admin)
  getTemplates: (activeOnly?: boolean) =>
    api.get('/document-review/templates', { params: { activeOnly } }),

  getTemplate: (id: string) =>
    api.get(`/document-review/templates/${id}`),

  createTemplate: (templateData: any) =>
    api.post('/document-review/templates', templateData),

  uploadTemplate: (formData: FormData) =>
    api.post('/document-review/templates/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),

  updateTemplate: (id: string, templateData: any) =>
    api.put(`/document-review/templates/${id}`, templateData),

  deleteTemplate: (id: string) =>
    api.delete(`/document-review/templates/${id}`),

  checkReacceptanceImpact: (payload: { action: 'create' | 'update' | 'delete'; id?: string; data?: any }) =>
    api.post('/document-review/templates/reacceptance-check', payload),

  // User Document Review
  getPendingDocuments: (memberProfileId: string) =>
    api.get(`/document-review/pending/${memberProfileId}`),

  getDocumentContent: (id: string) =>
    api.get(`/document-review/content/${id}`),

  downloadDocument: (id: string) =>
    api.get(`/document-review/download/${id}`, { responseType: 'blob' }),

  acceptDocument: (memberProfileId: string, documentTemplateId: string) =>
    api.post('/document-review/accept', { memberProfileId, documentTemplateId }),

  getAcceptances: (memberProfileId: string) =>
    api.get(`/document-review/acceptances/${memberProfileId}`),

  getReviewStatus: (memberProfileId: string) =>
    api.get(`/document-review/status/${memberProfileId}`),
};

// Guest Pass API
export const guestPassAPI = {
  create: (data: any) =>
    api.post('/guest-passes', data),

  getAll: (filters?: {
    memberProfileId?: string;
    guestEmail?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
  }) =>
    api.get('/guest-passes', { params: filters }),

  getActiveMemberPasses: (memberProfileId: string) =>
    api.get(`/guest-passes/member/${memberProfileId}/active`),

  getById: (id: string) =>
    api.get(`/guest-passes/${id}`),

  getByCode: (code: string) =>
    api.get(`/guest-passes/code/${code}`),

  redeem: (id: string) =>
    api.post(`/guest-passes/${id}/redeem`),

  cancel: (id: string) =>
    api.post(`/guest-passes/${id}/cancel`),

  validateGuestAccess: (guestEmail: string) =>
    api.get('/guest-passes/validate/guest-access', { params: { guestEmail } }),

  validateMemberSponsorship: (memberProfileId: string) =>
    api.get(`/guest-passes/validate/member-sponsorship/${memberProfileId}`),

  updateExpiredPasses: () =>
    api.post('/guest-passes/admin/update-expired'),

  getQRCode: (id: string) =>
    api.get(`/guest-passes/${id}/qr-code`, { responseType: 'arraybuffer' }),
};

// Transaction Processor API
export const transactionProcessorAPI = {
  getLogs: (limit?: number) =>
    api.get('/transaction-processor/logs', { params: { limit } }),
};

// Add-On API
export const addOnAPI = {
  // Admin: Manage add-ons
  create: (data: any) =>
    api.post('/add-ons', data),

  getAll: (activeOnly?: boolean) =>
    api.get('/add-ons', { params: activeOnly ? { activeOnly: 'true' } : {} }),

  getById: (id: string) =>
    api.get(`/add-ons/${id}`),

  update: (id: string, data: any) =>
    api.put(`/add-ons/${id}`, data),

  delete: (id: string) =>
    api.delete(`/add-ons/${id}`),

  // Get all assignments for an add-on (admin only)
  getAssignments: (id: string) =>
    api.get(`/add-ons/${id}/assignments`),

  // Available add-ons for a member
  getAvailable: (memberProfileId: string) =>
    api.get('/add-ons/available', { params: { memberProfileId } }),

  // Member subscriptions
  getMemberAddOns: (memberProfileId: string) =>
    api.get(`/add-ons/member/${memberProfileId}`),

  previewSubscription: (memberProfileId: string, addOnId: string) =>
    api.get(`/add-ons/member/${memberProfileId}/preview`, { params: { addOnId } }),

  subscribe: (memberProfileId: string, addOnId: string) =>
    api.post(`/add-ons/member/${memberProfileId}/subscribe`, { addOnId }),

  unsubscribe: (memberProfileId: string, addOnId: string) =>
    api.delete(`/add-ons/member/${memberProfileId}/unsubscribe/${addOnId}`),
};

// Check-in API
export const checkinAPI = {
  create: (data: { userId: string; memberProfileId?: string; facilityArea?: string; notes?: string }) =>
    api.post('/checkins', data),

  getRecent: (params?: { limit?: number; offset?: number }) =>
    api.get('/checkins/recent', { params }),

  getAll: (filters?: { userId?: string; memberProfileId?: string; facilityArea?: string; startDate?: string; endDate?: string; page?: number; limit?: number }) =>
    api.get('/checkins', { params: filters }),
};

// GroupPlay API
export const groupPlayAPI = {
  // Get distinct group play types
  getGroupPlayTypes: () => api.get('/group-plays/types'),

  // Get all group plays
  getGroupPlays: (params?: {
    status?: string;
    startDate?: string;
    endDate?: string;
  }) => api.get('/group-plays', { params }),

  // Get group play by ID
  getGroupPlayById: (groupPlayId: string) =>
    api.get(`/group-plays/${groupPlayId}`),

  // Create group play
  createGroupPlay: (data: any) =>
    api.post('/group-plays', data),

  // Update group play
  updateGroupPlay: (groupPlayId: string, data: any) =>
    api.put(`/group-plays/${groupPlayId}`, data),

  // Delete group play
  deleteGroupPlay: (groupPlayId: string) =>
    api.delete(`/group-plays/${groupPlayId}`),

  // Check which instances would be affected by changing end date
  checkAffectedInstancesByEndDate: (groupPlayId: string, newEndDate: string) =>
    api.post(`/group-plays/${groupPlayId}/check-affected-instances`, { newEndDate }),

  // Get group play instances
  getGroupPlayInstances: (groupPlayId: string, params?: {
    startDate?: string;
    endDate?: string;
  }) => api.get(`/group-plays/${groupPlayId}/instances`, { params }),

  // Get all instances across all group plays in a date range (max 92 days).
  // Each instance includes counts, venueNames and a parentGroupPlay summary.
  getGroupPlaySchedule: (params: {
    startDate: string;
    endDate: string;
  }) => api.get('/group-plays/instances', { params }),

  // Get group play instance by ID
  getGroupPlayInstanceById: (groupPlayId: string, instanceId: string) =>
    api.get(`/group-plays/${groupPlayId}/instances/${instanceId}`),

  // Update group play instance
  updateGroupPlayInstance: (groupPlayId: string, instanceId: string, data: any) =>
    api.put(`/group-plays/${groupPlayId}/instances/${instanceId}`, data),

  // Cancel group play instance
  cancelGroupPlayInstance: (groupPlayId: string, instanceId: string) =>
    api.delete(`/group-plays/${groupPlayId}/instances/${instanceId}`),

  // Book a group play instance
  bookGroupPlayInstance: (groupPlayId: string, instanceId: string, data: {
    memberProfileId?: string;
    guestName?: string;
    guestEmail?: string;
    notes?: string;
  }) => api.post(`/group-plays/${groupPlayId}/instances/${instanceId}/book`, data),

  // Admin: Register a member to a group play instance
  registerMemberToGroupPlay: (groupPlayId: string, instanceId: string, data: {
    memberProfileId: string;
    notes?: string;
  }) => api.post(`/group-plays/${groupPlayId}/instances/${instanceId}/book`, data),

  // Get group play instance bookings
  getGroupPlayInstanceBookings: (groupPlayId: string, instanceId: string) =>
    api.get(`/group-plays/${groupPlayId}/instances/${instanceId}/bookings`),

  // Cancel group play instance booking
  cancelGroupPlayInstanceBooking: (groupPlayId: string, instanceId: string, bookingId: string, data?: any) =>
    api.delete(`/group-plays/${groupPlayId}/instances/${instanceId}/bookings/${bookingId}`, { data }),

  // Get current user's group play bookings
  getUserBookings: () =>
    api.get('/group-plays/my-bookings'),
};

// Personal Training Type API
export const personalTrainingTypeAPI = {
  getAll: (showInactive?: boolean) =>
    api.get('/personal-training-types', { params: showInactive ? { showInactive: 'true' } : {} }),

  getById: (id: string) =>
    api.get(`/personal-training-types/${id}`),

  create: (data: { title: string; defaultPrice: number }) =>
    api.post('/personal-training-types', data),

  update: (id: string, data: { title?: string; defaultPrice?: number; active?: boolean }) =>
    api.put(`/personal-training-types/${id}`, data),

  delete: (id: string) =>
    api.delete(`/personal-training-types/${id}`),
};

// Personal Training API
export const personalTrainingAPI = {
  getAll: (filters?: { coachId?: string; startDate?: string; endDate?: string; status?: string }) =>
    api.get('/personal-trainings', { params: filters }),

  getById: (id: string) =>
    api.get(`/personal-trainings/${id}`),

  create: (data: {
    name: string;
    description?: string;
    coachId: string;
    typeId: string;
    venue: string;
    sessionDate: string;
    startTime: string;
    endTime: string;
    priceOverride?: number;
    notes?: string;
  }) =>
    api.post('/personal-trainings', data),

  update: (id: string, data: any) =>
    api.put(`/personal-trainings/${id}`, data),

  delete: (id: string) =>
    api.delete(`/personal-trainings/${id}`),

  getEnrollments: (id: string) =>
    api.get(`/personal-trainings/${id}/enrollments`),

  enrollMember: (id: string, data: {
    memberProfileId: string;
    notes?: string;
    overrideCancellationPolicy?: boolean;
  }) =>
    api.post(`/personal-trainings/${id}/enrollments`, data),

  cancelEnrollment: (id: string, enrollmentId: string, data?: { override?: boolean }) =>
    api.delete(`/personal-trainings/${id}/enrollments/${enrollmentId}`, { data }),
};

// Personal Training Pack API
export const personalTrainingPackAPI = {
  getMemberPacks: (memberProfileId: string) =>
    api.get(`/personal-training-packs/member/${memberProfileId}`),

  getActivePack: (memberProfileId: string) =>
    api.get(`/personal-training-packs/member/${memberProfileId}/active`),

  assignPack: (data: {
    memberProfileId: string;
    title: string;
    totalSessions: number;
    cost: number;
    notes?: string;
    expirationDate?: string;
  }) =>
    api.post('/personal-training-packs', data),

  archivePack: (id: string) =>
    api.put(`/personal-training-packs/${id}/archive`),

  getRedemptions: (id: string) =>
    api.get(`/personal-training-packs/${id}/redemptions`),
};

// System Notification API
export const systemNotificationAPI = {
  getAll: () =>
    api.get('/system-notifications'),

  create: (data: {
    title: string;
    message: string;
    type: 'informational' | 'warning' | 'emergency';
    startDate: string;
    tenantIds: string[];
    isActive: boolean;
  }) =>
    api.post('/system-notifications', data),

  update: (id: string, data: {
    title?: string;
    message?: string;
    type?: 'informational' | 'warning' | 'emergency';
    startDate?: string;
    tenantIds?: string[];
    isActive?: boolean;
  }) =>
    api.put(`/system-notifications/${id}`, data),

  delete: (id: string) =>
    api.delete(`/system-notifications/${id}`),

  getActive: () =>
    api.get('/system-notifications/active'),
};

// Gift Card API
export const giftCardAPI = {
  getAll: (page = 1, limit = 200, status: 'all' | 'active' | 'redeemed' = 'all') =>
    api.get('/gift-cards', { params: { page, limit, status } }),

  getById: (id: string) =>
    api.get(`/gift-cards/${id}`),

  create: (data: { denomination: number; notes?: string; expiresAt?: string }) =>
    api.post('/gift-cards', data),

  lookupByCode: (code: string) =>
    api.get(`/gift-cards/lookup/${encodeURIComponent(code)}`),

  emailGiftCard: (id: string, data: { recipientEmail: string; recipientName?: string }) =>
    api.post(`/gift-cards/${id}/email`, data),
};

// Wallet API
export const walletAPI = {
  getBalance: (memberId: string) =>
    api.get(`/wallet/${memberId}/balance`),

  getTransactions: (memberId: string, page = 1, limit = 50) =>
    api.get(`/wallet/${memberId}/transactions`, { params: { page, limit } }),

  addCredit: (memberId: string, data: { amount: number; notes?: string }) =>
    api.post(`/wallet/${memberId}/add-credit`, data),

  deleteCredit: (memberId: string, transactionId: string) =>
    api.delete(`/wallet/${memberId}/transactions/${transactionId}`),

  redeemGiftCard: (memberId: string, data: { code: string }) =>
    api.post(`/wallet/${memberId}/redeem-gift-card`, data),
};

export default api;
